import styled from '@emotion/styled';

import { spacing, borderRadius, transitions } from '../tokens';

/**
 * Valores visuais EXATOS lidos do Figma (nodes 9457:3184 não-selecionado e
 * 9457:3322 selecionado). Mantidos locais ao componente: divergem dos tokens
 * globais (ex.: borda selecionada #000FFF vs token primary #000eff) e trocar
 * os tokens globais seria breaking para todo o DS. Centralizados aqui para não
 * espalhar magic values. Ver memória "design-token-spec-divergence".
 */
export const spec = {
  card: {
    idle: { bg: '#F2F2F2', border: '#D9D9D9' },
    selected: { bg: '#E5E7FF', border: '#000FFF' },
  },
  iconCircle: {
    idle: { bg: '#D9D9D9', color: '#8C8C8C' },
    selected: { bg: '#CCCFFF', color: '#000FFF' },
  },
  text: '#3D3D3D',
  info: '#0B29FB',
  divider: '#D9D9D9',
};

type SelectedProp = { $selected: boolean };

/**
 * Card raiz: linha, padding 16px, radius 8px, borda 1px. Fundo/borda mudam
 * conforme `selected`. Vira foco/teclado quando interativo.
 */
export const StyledCard = styled('div', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<SelectedProp & { $interactive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.lg};
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing.lg}; /* 16px */
  border-radius: ${borderRadius.lg}; /* 8px */
  background-color: ${({ $selected }) =>
    $selected ? spec.card.selected.bg : spec.card.idle.bg};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? spec.card.selected.border : spec.card.idle.border};
  transition: ${transitions.base};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

  &:focus-visible {
    outline: 3px solid ${spec.card.selected.border};
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing.md};
  }
`;

/**
 * Grupo da esquerda: ícone + identidade (título/subtítulo).
 */
export const CardLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  min-width: 0;
`;

/**
 * Círculo do ícone à esquerda (48px). Define `color` para o ícone-slot
 * herdar via currentColor.
 */
export const IconCircle = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<SelectedProp>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: ${borderRadius.full};
  background-color: ${({ $selected }) =>
    $selected ? spec.iconCircle.selected.bg : spec.iconCircle.idle.bg};
  color: ${({ $selected }) =>
    $selected ? spec.iconCircle.selected.color : spec.iconCircle.idle.color};
  transition: ${transitions.base};

  svg {
    width: 24px;
    height: 24px;
  }
`;

/**
 * Identidade: título + subtítulo empilhados.
 */
export const Identity = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

/**
 * Grupo da direita: divisória + pares de detalhe.
 */
export const CardRight = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing['5xl']}; /* 48px — divisória → detalhes (Figma) */
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

/**
 * Divisória vertical tracejada (41px), #D9D9D9.
 */
export const Divider = styled('span')`
  width: 0;
  height: 41px;
  border-left: 1px dashed ${spec.divider};
  flex-shrink: 0;

  @media (max-width: 640px) {
    display: none;
  }
`;

/**
 * Linha de pares de detalhe (gap 48px entre pares).
 */
export const Details = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${spacing['5xl']}; /* 48px */

  @media (max-width: 640px) {
    gap: ${spacing['2xl']};
    flex-wrap: wrap;
  }
`;

/**
 * Par individual: label (+ ícone info) em cima, valor embaixo.
 */
export const DetailPair = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

/**
 * Box do checkbox nas cards de seleção múltipla.
 * Borda #262626 unchecked / #000FFF checked, fundo sempre #FFFFFF.
 */
export const CardCheckboxBox = styled('span', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<SelectedProp>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: ${borderRadius.sm};
  border: 1.5px solid ${({ $selected }) =>
    $selected ? spec.card.selected.border : '#262626'};
  background-color: ${({ $selected }) =>
    $selected ? spec.card.selected.border : 'transparent'};
  color: #ffffff;
  transition: ${transitions.base};

  svg {
    width: 12px;
    height: 12px;
  }
`;

/**
 * Linha do label com o ícone de info.
 */
export const DetailLabelRow = styled('div')`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

/**
 * Botão/wrapper do ícone de info (ⓘ). Sempre acessível por título/aria.
 */
export const InfoIconWrapper = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${spec.info};
  line-height: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;
